import { ChangeEvent, FunctionComponent } from 'react';
import { Box, Input, Text } from '@chakra-ui/react';

type EditableNumberCellProps = {
  value: number;
  isEditing: boolean;
  isDisabled?: boolean;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  ariaLabel: string;
};

/**
 * Célula que vira input number em modo edição. Em view mostra o valor centrado.
 * Aceita só inteiros >= 0 por padrão; valores inválidos viram 0.
 */
export const EditableNumberCell: FunctionComponent<EditableNumberCellProps> = ({
  value,
  isEditing,
  isDisabled,
  onChange,
  min = 0,
  max,
  ariaLabel,
}) => {
  if (!isEditing) {
    return (
      <Box
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        px={2}
        py={1}
        textAlign="center"
        bg="white"
      >
        <Text fontSize="sm" fontWeight="semibold" color="gray.800">
          {value}
        </Text>
      </Box>
    );
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    const parsed = raw === '' ? 0 : Number(raw);
    if (!Number.isFinite(parsed)) return;
    const clamped = Math.max(min, max !== undefined ? Math.min(max, parsed) : parsed);
    onChange?.(Math.trunc(clamped));
  };

  return (
    <Input
      type="number"
      size="sm"
      textAlign="center"
      value={Number.isFinite(value) ? value : 0}
      onChange={handleChange}
      isDisabled={isDisabled}
      min={min}
      max={max}
      aria-label={ariaLabel}
      px={2}
      _focus={{ borderColor: 'brand.500', boxShadow: 'outline' }}
    />
  );
};
